import { useEffect } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { useT } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";
import { fiscalInputClass } from "./Shell.js";

export type FiscalYearOption = {
  id: number;
  label: string;
  startDate: string;
  endDate: string;
  status: "draft" | "open" | "closed";
};

/**
 * Seletor combinado de empresa + exercício fiscal.
 *
 * Lista empresas a que o usuário tem acesso, e dentro da empresa selecionada
 * lista os fiscal_years ordenados por start_date DESC. Avisa quando a empresa
 * ainda não tem FYs criados.
 */
export function FiscalYearSelector({
  companyId,
  fiscalYearId,
  onChangeCompany,
  onChangeFiscalYear,
}: {
  companyId: number | null;
  fiscalYearId: number | null;
  onChangeCompany: (companyId: number) => void;
  onChangeFiscalYear: (fy: FiscalYearOption | null) => void;
}) {
  const { user } = useAuth();
  const t = useT();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  // F-fiscal-default — auto-seleciona a 1ª empresa acessível (= principal, pela
  // ordem de companies.list) quando nenhuma está escolhida. Sem isso a página
  // nascia sem empresa → o seletor de exercício ficava desabilitado e parecia
  // "sem exercício". Só age com companyId null; após selecionar, não re-dispara.
  const firstCompanyId = accessibleCompanies[0]?.id ?? null;
  useEffect(() => {
    if (companyId === null && firstCompanyId !== null) {
      onChangeCompany(firstCompanyId);
    }
  }, [companyId, firstCompanyId, onChangeCompany]);

  const fyQuery = trpc.fiscalYears.list.useQuery(
    { companyId: companyId ?? 0, limit: 50, offset: 0 },
    { enabled: companyId !== null },
  );

  const rows = fyQuery.data?.rows ?? [];

  // F-fiscal-default — auto-seleciona o exercício mais recente quando há empresa
  // mas nenhum FY escolhido. Sem isto o <select> exibe o 1º option visualmente,
  // mas fiscalYearId fica null e a página continua em "Selecione um exercício".
  const firstFy = rows[0] ?? null;
  useEffect(() => {
    if (companyId !== null && fiscalYearId === null && firstFy) {
      onChangeFiscalYear(firstFy);
    }
  }, [companyId, fiscalYearId, firstFy, onChangeFiscalYear]);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label className="text-xs uppercase tracking-wider text-neutral-500">
        {t("fiscalYearSelector.companyLabel")}
      </label>
      <select
        value={companyId ?? ""}
        onChange={(e) => onChangeCompany(Number(e.target.value))}
        className={fiscalInputClass + " min-w-48"}
      >
        {accessibleCompanies.length === 0 && (
          <option value="">{t("fiscalYearSelector.noAccessOption")}</option>
        )}
        {accessibleCompanies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      <label className="text-xs uppercase tracking-wider text-neutral-500">
        {t("fiscalYearSelector.fiscalYearLabel")}
      </label>
      <select
        value={fiscalYearId ?? ""}
        onChange={(e) => {
          const id = Number(e.target.value);
          const fy = rows.find((r) => r.id === id);
          onChangeFiscalYear(fy ?? null);
        }}
        className={fiscalInputClass + " min-w-56"}
        disabled={rows.length === 0 || fyQuery.isLoading}
      >
        {fyQuery.isLoading && <option value="">{t("fiscalYearSelector.loadingOption")}</option>}
        {!fyQuery.isLoading && rows.length === 0 && (
          <option value="">{t("fiscalYearSelector.noFiscalYearOption")}</option>
        )}
        {rows.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label} · {r.startDate} → {r.endDate} · {r.status}
          </option>
        ))}
      </select>
      {fyQuery.error && <span className="text-xs text-red-400">{fyQuery.error.message}</span>}
    </div>
  );
}
